import * as React from "react";

import { Grid, Table, Button, Input as InputField, Icon, Form, InputOnChangeData } from "semantic-ui-react";
import { format } from "date-fns";
import { useSelector, useDispatch } from "react-redux";
import { validators } from "not-valid";

import { Input } from "@neworbit/simpleui-input";

import { toast } from "@teamprevent/common/toasts";

import { ValidationResultType } from "not-valid/bin/results";

import { UserSearchListModel } from "../model";
import { loadUsers } from "../actions";
import {
    emailAddressSelector,
    employeeUniqueIdSelector,
    phoneNumberSelector,
    dateOfBirthSelector
} from "../selectors";

import { Link, Query, replace } from "redux-little-router";

export const SearchUsers: React.FunctionComponent = () => {
    const emailAddressFromQuery = useSelector(emailAddressSelector);
    const employeeUniqueIdFromQuery = useSelector(employeeUniqueIdSelector);
    const phoneNumberFromQuery = useSelector(phoneNumberSelector);
    const dateOfBirthFromQuery = useSelector(dateOfBirthSelector);
    const [emailAddress, setEmailAddress] = React.useState<string | undefined>(emailAddressFromQuery);
    const [employeeUniqueId, setEmployeeUniqueId] = React.useState<string | undefined>(employeeUniqueIdFromQuery);
    const [phoneNumber, setPhoneNumber] = React.useState<string | undefined>(phoneNumberFromQuery);
    const [dateOfBirth, setDateOfBirth] = React.useState<string | undefined>(dateOfBirthFromQuery);
    const [searching, setSearching] = React.useState<boolean>(false);
    const [users, setUsers] = React.useState<UserSearchListModel[]>([]);
    const [noMatchingResults, setNoMatchingResults] = React.useState<boolean>(false);

    const [initialLoadNeeded, setInitialLoadNeeded] = React.useState(
        !!emailAddressFromQuery || !!employeeUniqueIdFromQuery || !!phoneNumberFromQuery);
    const dispatch = useDispatch();

    const setQueryParams = React.useCallback(() => {
        const query: Query = {};

        // Clear the query params first before setting new ones
        dispatch(replace({ query }, { persistQuery: false }));

        if (emailAddress) {
            query.emailAddress = emailAddress;
        }

        if (employeeUniqueId) {
            query.employeeUniqueId = employeeUniqueId;
        }

        if (phoneNumber) {
            query.phoneNumber = phoneNumber;
        }

        if (dateOfBirth) {
            query.dateOfBirth = dateOfBirth;
        }

        dispatch(replace({ query }, { persistQuery: true }));
    }, [dateOfBirth, dispatch, emailAddress, employeeUniqueId, phoneNumber]);

    const onSearch = React.useCallback(async (
        emailAddressParam: string,
        employeeUniqueIdParam: string,
        phoneNumberParam: string,
        dateOfBirthParam: string) => {
        if (!emailAddressParam && !employeeUniqueIdParam && !phoneNumberParam && !dateOfBirthParam) {
            toast.info("Please provide at-least 1 search criteria");
            return;
        }

        if (phoneNumberParam) {
            const phoneNumberValid = validators.validPhoneNumber(10)(phoneNumberParam);

            if (phoneNumberValid.type === ValidationResultType.Fail) {
                toast.warning("Please enter a valid phone number or remove it");
                return;
            };
        }

        setSearching(true);
        setNoMatchingResults(false);

        setQueryParams();

        try {
            const result = await loadUsers({
                emailAddress: emailAddressParam,
                secondaryReference: employeeUniqueIdParam,
                phoneNumber: phoneNumberParam,
                dateOfBirth: dateOfBirthParam
            });
            setUsers(result);
            if (result.length === 0) {
                setNoMatchingResults(true);
            }
        }
        catch {
            toast.error("Users search threw an error");
        }
        finally {
            setSearching(false);
        }
    }, [setQueryParams]);

    const getUserUrl = (user: UserSearchListModel) => {
        if (user.userType === "Employee") {
            return `customers/${user.customerId}/employeeRecords/${user.id}`;
        } else {
            return `customers/${user.customerId}/customerUsers/${user.id}`;
        }
    };

    React.useEffect(() => {
        if (initialLoadNeeded) {
            onSearch(emailAddressFromQuery || "", employeeUniqueIdFromQuery || "", phoneNumberFromQuery || "", dateOfBirthFromQuery || "");
            setInitialLoadNeeded(false);
        }
    }, [dateOfBirthFromQuery, emailAddressFromQuery, employeeUniqueIdFromQuery, initialLoadNeeded, onSearch, phoneNumberFromQuery]);

    const onEmailAddressChange = React.useCallback((value: string) => setEmailAddress(value), []);
    const onEmployeeUniqueIdChange = React.useCallback((value: string) => setEmployeeUniqueId(value), []);
    const onPhoneNumberChange = React.useCallback((value: string) => setPhoneNumber(value), []);

    const onDateOfBirthChange = React.useCallback((event: React.SyntheticEvent<HTMLInputElement>, data: InputOnChangeData) => {
        setDateOfBirth(data.value);
    }, []);

    const onSearchClick = React.useCallback(() => {
        onSearch(emailAddress || "", employeeUniqueId || "", phoneNumber || "", dateOfBirth || "");
    }, [dateOfBirth, emailAddress, employeeUniqueId, onSearch, phoneNumber]);

    return (
        <>
            <Grid container>
                <Grid.Row>
                    <Grid.Column width={16}>
                        <h1>User Search</h1>
                    </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                    <Grid.Column className="filters">
                        <Form>
                            <Grid columns={"5"} stackable>
                                <Grid.Column>
                                    <Input.Text value={emailAddress} label="Email Address" onChange={onEmailAddressChange} />
                                </Grid.Column>
                                <Grid.Column>
                                    <Input.Text value={employeeUniqueId} label="Employee Unique ID" onChange={onEmployeeUniqueIdChange} />
                                </Grid.Column>
                                <Grid.Column>
                                    <Input.Text
                                        value={phoneNumber}
                                        label="Phone Number"
                                        onChange={onPhoneNumberChange}
                                        validation={[validators.validPhoneNumber(10)]}
                                    />
                                </Grid.Column>
                                <Grid.Column>
                                    <Form.Field>
                                        <label>Date of Birth</label>
                                        <InputField value={dateOfBirth} onChange={onDateOfBirthChange} type="date" />
                                    </Form.Field>
                                </Grid.Column>
                                <Grid.Column verticalAlign="bottom">
                                    <Button
                                        content="Search"
                                        loading={searching}
                                        disabled={searching}
                                        onClick={onSearchClick}
                                    />
                                </Grid.Column>
                            </Grid>
                        </Form>
                    </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                    <Grid.Column width={16}>
                        <Table>
                            <Table.Header>
                                <Table.Row>
                                    <Table.HeaderCell>First Name</Table.HeaderCell>
                                    <Table.HeaderCell>Last Name</Table.HeaderCell>
                                    <Table.HeaderCell>Mobile Phone Number</Table.HeaderCell>
                                    <Table.HeaderCell>Personal Email Address</Table.HeaderCell>
                                    <Table.HeaderCell>Work Email Address</Table.HeaderCell>
                                    <Table.HeaderCell>Registered Email Address</Table.HeaderCell>
                                    <Table.HeaderCell>Date Of Birth</Table.HeaderCell>
                                    <Table.HeaderCell>Customer Name</Table.HeaderCell>
                                    <Table.HeaderCell>Employee Unique ID</Table.HeaderCell>
                                    <Table.HeaderCell>User Type</Table.HeaderCell>
                                </Table.Row>
                            </Table.Header>
                            <Table.Body>
                                {users && users.map(user => (
                                    <Table.Row key={user.id}>
                                        <Table.Cell>
                                            <Link href={getUserUrl(user)}>
                                                {user.firstName}
                                            </Link>
                                        </Table.Cell>

                                        <Table.Cell>
                                            <Link href={getUserUrl(user)}>
                                                {user.lastName}
                                            </Link>
                                        </Table.Cell>

                                        <Table.Cell>
                                            <Link href={getUserUrl(user)}>
                                                {user.mobilePhoneNumber}
                                            </Link>
                                        </Table.Cell>

                                        <Table.Cell>
                                            <Link href={getUserUrl(user)}>
                                                {user.personalEmailAddress}
                                            </Link>
                                        </Table.Cell>

                                        <Table.Cell>
                                            <Link href={getUserUrl(user)}>
                                                {user.workEmailAddress}
                                            </Link>
                                        </Table.Cell>

                                        <Table.Cell>
                                            <Link href={getUserUrl(user)}>
                                                {user.registeredEmailAddress}
                                            </Link>
                                        </Table.Cell>

                                        <Table.Cell>
                                            <Link href={getUserUrl(user)}>
                                                {user.dateOfBirth && format(user.dateOfBirth, "dd/MM/yyyy")}
                                            </Link>
                                        </Table.Cell>

                                        <Table.Cell>
                                            <Link href={getUserUrl(user)}>
                                                {user.customerName}
                                            </Link>
                                        </Table.Cell>

                                        <Table.Cell>
                                            <Link href={getUserUrl(user)}>
                                                {user.secondaryReference}
                                            </Link>
                                        </Table.Cell>
                                        <Table.Cell>
                                            <Link href={getUserUrl(user)}>
                                                {user.userType}
                                            </Link>
                                        </Table.Cell>
                                    </Table.Row>
                                ))}
                            </Table.Body>
                            {noMatchingResults &&
                                <Table.Footer>
                                    <Table.Row>
                                        <Table.HeaderCell colSpan="4">
                                            <InputField attached="bottom" info>
                                                <Icon name="info" />
                                                No users found matching the search criteria.
                                            </InputField>
                                        </Table.HeaderCell>
                                    </Table.Row>
                                </Table.Footer>
                            }
                        </Table>
                    </Grid.Column>
                </Grid.Row>
            </Grid>
        </>
    );
};
